import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton,
    InputAdornment, ListItemIcon, Menu, MenuItem, Paper, Switch,
    TextField, Tooltip, Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import { useTranslation } from 'react-i18next';
import Layout from '../components/layout/Layout';
import PageHeader from '../components/layout/PageHeader';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { AddTile, InitialsAvatar } from '../components/common/ui';
import UserService from '../services/UserService';
import { errorMessage, fieldErrors } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { fmtDate } from '../utils/format';
import { usePageRefresh } from '../components/layout/pullToRefresh';

const STATUS_STYLE = {
    ACTIVE: { color: '#059669', bg: 'rgba(16,185,129,0.12)' },
    INVITED: { color: '#B45309', bg: 'rgba(245,158,11,0.14)' },
    INACTIVE: { color: '#64748B', bg: '#F1F5F9' },
};

function Badge({ children, color, bg }) {
    return (
        <Typography component="span" sx={{ fontSize: 11.5, fontWeight: 700, px: 1, py: 0.3, borderRadius: '6px', whiteSpace: 'nowrap', color, bgcolor: bg }}>
            {children}
        </Typography>
    );
}

const EMPTY = { firstName: '', lastName: '', email: '', role: 'EMPLOYEE', active: true };

/** Invite form (new person) or edit form (name, role, active). */
function UserDialog({ open, user, onClose, onInvited, onSaved }) {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const [form, setForm] = useState(EMPTY);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setErrors({});
        setForm(user ? { firstName: user.firstName, lastName: user.lastName, email: user.email, role: user.role === 'ADMIN' ? 'ADMIN' : 'EMPLOYEE', active: user.status !== 'INACTIVE' } : EMPTY);
    }, [open, user]);

    const set = (k) => (e) => { setForm(f => ({ ...f, [k]: e.target.value })); setErrors(x => ({ ...x, [k]: undefined })); };

    const save = async () => {
        const v = {};
        if (!form.firstName.trim()) v.firstName = t('validation.required');
        if (!form.lastName.trim()) v.lastName = t('validation.required');
        if (!user && !/^\S+@\S+\.\S+$/.test(form.email.trim())) v.email = t('validation.email');
        setErrors(v);
        if (Object.keys(v).length) return;
        setSaving(true);
        try {
            if (user) {
                onSaved(await UserService.update(user.id, { firstName: form.firstName, lastName: form.lastName, role: form.role, active: form.active }));
            } else {
                onInvited(await UserService.invite({ firstName: form.firstName, lastName: form.lastName, email: form.email.trim(), role: form.role }));
            }
        } catch (err) {
            const fe = fieldErrors(err);
            if (Object.keys(fe).length) setErrors(fe);
            else showToast(errorMessage(err, t('common.error')), 'error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
            <DialogTitle sx={{ fontWeight: 700, fontSize: 18 }}>{user ? t('team.editTitle') : t('team.inviteTitle')}</DialogTitle>
            <DialogContent>
                {!user && <Typography sx={{ fontSize: 13.5, color: '#64748B', mb: 2 }}>{t('team.inviteHint')}</Typography>}
                <Grid container spacing={2} sx={{ pt: 1 }}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField fullWidth size="small" required autoFocus label={t('auth.fields.firstName')} value={form.firstName} onChange={set('firstName')}
                                   error={!!errors.firstName} helperText={errors.firstName} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField fullWidth size="small" required label={t('auth.fields.lastName')} value={form.lastName} onChange={set('lastName')}
                                   error={!!errors.lastName} helperText={errors.lastName} />
                    </Grid>
                    <Grid size={12}>
                        <TextField fullWidth size="small" required type="email" label={t('auth.fields.email')} value={form.email} onChange={set('email')}
                                   disabled={!!user} error={!!errors.email} helperText={errors.email} />
                    </Grid>
                    <Grid size={12}>
                        <TextField select fullWidth size="small" label={t('team.role')} value={form.role} onChange={set('role')}
                                   helperText={t(`team.roleHint.${form.role}`)}>
                            <MenuItem value="EMPLOYEE">{t('team.roles.EMPLOYEE')}</MenuItem>
                            <MenuItem value="ADMIN">{t('team.roles.ADMIN')}</MenuItem>
                        </TextField>
                    </Grid>
                    {user && user.status !== 'INVITED' && (
                        <Grid size={12}>
                            <FormControlLabel control={<Switch checked={form.active} onChange={(e) => setForm(f => ({ ...f, active: e.target.checked }))} />}
                                              label={<Typography sx={{ fontSize: 14 }}>{t('team.activeLabel')}</Typography>} />
                            <Typography sx={{ fontSize: 12.5, color: '#94A3B8', ml: 6, mt: -0.5 }}>{t('team.activeHint')}</Typography>
                        </Grid>
                    )}
                </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
                <Button onClick={onClose} disabled={saving} sx={{ color: '#64748B' }}>{t('common.cancel')}</Button>
                <Button variant="contained" onClick={save} disabled={saving} disableElevation sx={{ minWidth: 120 }}>
                    {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : (user ? t('common.save') : t('team.sendInvite'))}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

/** After an invite: whether the email went out, plus the link to share another way. */
function InviteLinkDialog({ result, onClose }) {
    const { t } = useTranslation();
    const { showToast } = useToast();
    if (!result) return null;
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(result.setupUrl);
            showToast(t('team.linkCopied'), 'success');
        } catch {
            showToast(t('team.copyFailed'), 'error');
        }
    };
    const share = () => navigator.share?.({ title: t('team.shareTitle'), url: result.setupUrl }).catch(() => {});
    return (
        <Dialog open onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
            <DialogTitle sx={{ fontWeight: 700, fontSize: 18 }}>{t('team.linkTitle', { name: result.user.firstName })}</DialogTitle>
            <DialogContent>
                {result.emailSent ? (
                    <Alert icon={<MarkEmailReadOutlinedIcon />} severity="success" sx={{ mb: 2 }}>{t('team.emailSent', { email: result.user.email })}</Alert>
                ) : (
                    <Alert severity="warning" sx={{ mb: 2 }}>{t('team.emailNotSent')}</Alert>
                )}
                <Typography sx={{ fontSize: 13.5, color: '#475569', mb: 1 }}>{t('team.linkHint')}</Typography>
                <TextField fullWidth size="small" value={result.setupUrl} slotProps={{
                    htmlInput: { readOnly: true, onFocus: (e) => e.target.select() },
                    input: { endAdornment: <InputAdornment position="end"><Tooltip title={t('team.copyLink')}><IconButton size="small" onClick={copy}><ContentCopyIcon fontSize="small" /></IconButton></Tooltip></InputAdornment> },
                }} />
                <Typography sx={{ fontSize: 12.5, color: '#94A3B8', mt: 1 }}>{t('team.linkExpires', { date: fmtDate(result.expiresAt) })}</Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
                {typeof navigator.share === 'function' && <Button onClick={share}>{t('team.share')}</Button>}
                <Button onClick={copy} startIcon={<ContentCopyIcon />}>{t('team.copyLink')}</Button>
                <Button variant="contained" disableElevation onClick={onClose}>{t('common.ok')}</Button>
            </DialogActions>
        </Dialog>
    );
}

export default function TeamPage() {
    const { t } = useTranslation();
    const { user: me } = useAuth();
    const { showToast } = useToast();
    const [users, setUsers] = useState(null);
    const [dialog, setDialog] = useState(null);        // { user } or {} for a new invite
    const [linkResult, setLinkResult] = useState(null);
    const [menu, setMenu] = useState(null);            // { anchor, user }
    const [toDelete, setToDelete] = useState(null);

    const load = useCallback(() => {
        return UserService.list().then(setUsers).catch(() => showToast(t('common.loadError'), 'error'));
    }, [showToast, t]);
    useEffect(() => { load(); }, [load]);
    usePageRefresh(load);

    const resend = async (u) => {
        setMenu(null);
        try {
            setLinkResult(await UserService.resendInvite(u.id));
            load();
        } catch (err) {
            showToast(errorMessage(err, t('common.error')), 'error');
        }
    };

    const remove = async () => {
        try {
            await UserService.remove(toDelete.id);
            showToast(t('team.deleted'), 'success');
            load();
        } catch (err) {
            showToast(errorMessage(err, t('common.error')), 'error');
        } finally {
            setToDelete(null);
        }
    };

    const now = new Date();

    return (
        <Layout>
            <PageHeader title={t('team.title')} subtitle={t('team.subtitle')} count={users ? users.length : null}
                        action={<Button variant="contained" startIcon={<PersonAddAlt1OutlinedIcon />} onClick={() => setDialog({})}>{t('team.invite')}</Button>} />

            {!users ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>
            ) : (
                <Box sx={{
                    display: 'grid', gap: 2,
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' },
                }}>
                    <AddTile label={t('team.invite')} icon={<PersonAddAlt1OutlinedIcon fontSize="small" />} onClick={() => setDialog({})} minHeight={140} />
                    {users.map(u => {
                        const self = u.id === me?.id;
                        const expired = u.status === 'INVITED' && (!u.inviteExpiresAt || new Date(u.inviteExpiresAt) < now);
                        const st = STATUS_STYLE[u.status];
                        const admin = u.role === 'ADMIN';
                        return (
                            // Clicking a colleague's card edits them; your own account is changed under My account / Security.
                            <Paper key={u.id} elevation={0} onClick={self ? undefined : () => setDialog({ user: u })} sx={{
                                border: '1px solid #E2E8F0', borderRadius: '14px', p: 2.25, minHeight: 140, cursor: self ? 'default' : 'pointer',
                                display: 'flex', flexDirection: 'column', gap: 1.5, opacity: u.status === 'INACTIVE' ? 0.65 : 1,
                                transition: 'border-color 0.15s, box-shadow 0.15s',
                                '&:hover': self ? {} : { borderColor: '#CBD5E1', boxShadow: '0 8px 20px -14px rgba(30,58,82,0.45)' },
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                                    <InitialsAvatar name={`${u.firstName} ${u.lastName}`} />
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography noWrap sx={{ fontWeight: 700, fontSize: 14.5, color: '#0F172A' }}>
                                            {u.firstName} {u.lastName}
                                            {self && <Box component="span" sx={{ fontWeight: 500, color: '#94A3B8' }}> · {t('team.you')}</Box>}
                                        </Typography>
                                        <Typography noWrap sx={{ fontSize: 12.5, color: '#64748B' }}>{u.email}</Typography>
                                    </Box>
                                    {/* Always visible (touch devices have no hover). */}
                                    {!self && (
                                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); setMenu({ anchor: e.currentTarget, user: u }); }}
                                                    aria-label={t('common.more')} sx={{ color: '#94A3B8', alignSelf: 'flex-start', mt: -0.5, mr: -0.75 }}>
                                            <MoreHorizIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                </Box>
                                <Box sx={{ mt: 'auto', pt: 1.25, borderTop: '1px solid #F1F5F9', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                    <Badge color={admin ? '#427898' : '#475569'} bg={admin ? 'rgba(66,120,152,0.12)' : '#F1F5F9'}>
                                        {t(`team.roles.${admin ? 'ADMIN' : 'EMPLOYEE'}`)}
                                    </Badge>
                                    <Badge color={expired ? '#DC2626' : st.color} bg={expired ? 'rgba(239,68,68,0.10)' : st.bg}>
                                        {expired ? t('team.status.EXPIRED') : t(`team.status.${u.status}`)}
                                    </Badge>
                                    {u.status === 'INVITED' && !expired && (
                                        <Typography sx={{ fontSize: 11.5, color: '#94A3B8' }}>{t('team.linkExpires', { date: fmtDate(u.inviteExpiresAt) })}</Typography>
                                    )}
                                </Box>
                            </Paper>
                        );
                    })}
                </Box>
            )}

            <Menu anchorEl={menu?.anchor} open={!!menu} onClose={() => setMenu(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
                <MenuItem onClick={() => { setDialog({ user: menu.user }); setMenu(null); }} sx={{ fontSize: 14 }}>
                    <ListItemIcon><EditOutlinedIcon fontSize="small" /></ListItemIcon>{t('common.edit')}
                </MenuItem>
                {menu?.user.status === 'INVITED' && (
                    <MenuItem onClick={() => resend(menu.user)} sx={{ fontSize: 14 }}>
                        <ListItemIcon><SendOutlinedIcon fontSize="small" /></ListItemIcon>{t('team.resend')}
                    </MenuItem>
                )}
                {menu?.user.status === 'INVITED' && (
                    <MenuItem onClick={() => { setToDelete(menu.user); setMenu(null); }} sx={{ fontSize: 14, color: '#DC2626' }}>
                        <ListItemIcon sx={{ color: '#DC2626' }}><DeleteOutlineIcon fontSize="small" /></ListItemIcon>{t('team.cancelInvite')}
                    </MenuItem>
                )}
            </Menu>

            <UserDialog open={!!dialog} user={dialog?.user} onClose={() => setDialog(null)}
                        onInvited={(res) => { setDialog(null); setLinkResult(res); load(); }}
                        onSaved={() => { setDialog(null); showToast(t('team.saved'), 'success'); load(); }} />
            <InviteLinkDialog result={linkResult} onClose={() => setLinkResult(null)} />
            <ConfirmDialog open={!!toDelete} title={t('team.cancelInviteTitle')} confirmLabel={t('team.cancelInvite')}
                           text={t('team.cancelInviteText', { name: `${toDelete?.firstName || ''} ${toDelete?.lastName || ''}`.trim() })}
                           onConfirm={remove} onClose={() => setToDelete(null)} />
        </Layout>
    );
}
